import { hasAuthToken, mindboxBackendOperation } from './mindboxBackend';
import { mindboxLog } from './mindboxLog';
import type { BasketItem } from '../store/slices/basketSlice';
import type { IProductInfo } from '../modules/home/types/Menu';

/**
 * События Mindbox. Все уходят только через наш бэкенд (`POST /client/mb/*`):
 * операции через JS-трекер (`Telegram.*`) с фронта больше не отправляем.
 *
 * Бэкенду нужен токен — без него он ответит 401, поэтому гостя пропускаем.
 */

const skipGuest = (operation: string) => {
  mindboxLog({
    kind: 'skip',
    operation: `${operation} (backend)`,
    message: 'не отправлено: пользователь не авторизован',
  });
  return Promise.resolve(false);
};

/** Цена за единицу с учётом выбранных модификаторов. */
const unitPrice = (item: BasketItem) => {
  const stored = Number(item.total_price) || 0;
  const base = Number(item.org_price) || 0;
  const modifiers = (item.selectedModifiers ?? []).reduce(
    (total, modifier) => total + (Number(modifier.price) || 0) * modifier.quantity,
    0
  );

  return Math.max(stored, base + modifiers);
};

// ---------------------------------------------------------------------------
// Клиент
// ---------------------------------------------------------------------------

/**
 * Online.Authorization: привязывает устройство (deviceUUID) к клиенту.
 * Клиента бэкенд берёт из токена, в теле — только deviceUUID, его подставляет
 * mindboxBackendOperation.
 */
export const mindboxAuthorizeDevice = () => {
  if (!hasAuthToken()) return skipGuest('Online.Authorization');

  return mindboxBackendOperation('Online.Authorization', '/mb/auth-customer', {});
};

// ---------------------------------------------------------------------------
// Каталог
// ---------------------------------------------------------------------------

/** Просмотр категории — при нажатии на категорию. */
export const mindboxViewCategory = (category: { id: string | number }) => {
  if (!hasAuthToken()) return skipGuest('Online.ViewCategory');

  return mindboxBackendOperation('Online.ViewCategory', '/mb/view-category', {
    viewProductCategory: {
      productCategory: { ids: { productID: String(category.id) } },
    },
  });
};

/** Просмотр карточки блюда — при открытии карточки. */
export const mindboxViewProduct = (product: IProductInfo) => {
  const productID = String(product.sku || product.id);

  console.log(
    `[view-product] «${product.title}»: id=${product.id}, sku=${product.sku ?? '—'} → productID=${productID}` +
      (product.sku ? ' (SKU)' : ' (нет SKU — уходит id!)')
  );

  if (!hasAuthToken()) {
    console.log('[view-product] не отправлено: нет токена (гость)');
    return skipGuest('Online.ViewProduct');
  }

  // В номенклатуре Mindbox товары лежат под SKU, а не под id меню: с id
  // просмотр оседает как «Неизвестный продукт».
  //
  // Только товар, без productGroup: Mindbox не связывает действие
  // одновременно с продуктом и группой продуктов. Цену бэкенд ждёт всегда —
  // хотя бы 0.
  return mindboxBackendOperation('Online.ViewProduct', '/mb/view-product', {
    viewProduct: {
      price: Math.max(Number(product.org_price) || 0, 0),
      product: { ids: { productID } },
    },
  });
};

// ---------------------------------------------------------------------------
// Корзина
// ---------------------------------------------------------------------------

const SET_CART_MAX_LINES = 500;

/**
 * Состав корзины целиком (Online.SetCart), пустой список — очистка.
 * Всю корзину, а не дельту: Mindbox держит последнее состояние, и полный
 * список переживает пропущенное событие.
 */
export const mindboxSetCart = (items: BasketItem[]) => {
  if (!hasAuthToken()) {
    console.log('[set-cart] не отправлено: нет токена (гость)');
    return skipGuest('Online.SetCart');
  }

  // Одно блюдо может лежать в корзине несколькими строками (разные
  // модификаторы), а Mindbox принимает каждый productID только один раз —
  // иначе ProtocolError «Идентификатор продукта передан несколько раз».
  // Сводим такие строки в одну: количество складываем, цену берём за линию.
  const lines = new Map<string, { count: number; priceOfLine: number }>();
  for (const item of items) {
    if (!item.isAvailable || item.quantity <= 0) continue;
    // SKU, как и в просмотре товара: под ним товары лежат в
    // номенклатуре Mindbox. id меню — только для старых позиций без SKU.
    const productID = String(item.sku || item.id);
    const line = lines.get(productID) ?? { count: 0, priceOfLine: 0 };
    line.count += item.quantity;
    line.priceOfLine += unitPrice(item) * item.quantity;
    lines.set(productID, line);
  }

  return mindboxBackendOperation('Online.SetCart', '/mb/set-cart', {
    productList: [...lines]
      // Только цена за линию: вместе с pricePerItem Mindbox отвечает
      // ProtocolError «разрешено передавать либо цену за линию, либо цену
      // за один продукт». За линию — потому что у вариантов одного блюда
      // цена за единицу может отличаться.
      .map(([productID, { count, priceOfLine }]) => ({
        count,
        priceOfLine,
        product: { ids: { productID } },
      }))
      .slice(0, SET_CART_MAX_LINES),
  });
};
